import clsx from 'clsx';
import Icon from 'components/atoms/Icon';
import ListWithIcon from 'components/molecules/ListWithIcon/ListWithIcon';
import {
  ProjectPageGetProjectByIdDocument,
  ProjectStatus,
  useDeleteAssessmentStageMutation,
} from 'generated/graphql';
import { FC, useState } from 'react';
import {
  Button,
  IconType,
  Modal,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './DeleteStage.module.scss';

interface IDeleteStage {
  projectId: number;
  id: string;
  projectStatus: ProjectStatus;
  disabled?: boolean;
}

const MODAL_CONTENT: Record<ProjectStatus, { content: React.ReactNode }> = {
  [ProjectStatus.Published]: {
    content: (
      <>
        <p className={styles.modalContentRow}>
          Removing this stage will permanently delete all settings, results, and
          candidate access. This cannot be undone.
        </p>
        <p className={styles.modalContentRow}>
          Assessment stages cannot be added to a published role.
        </p>
        <div className={styles.listContainer}>
          <ListWithIcon
            icon={
              <div className={clsx(styles.iconContainer, styles.warningIcon)}>
                <Icon icon="warning" />
              </div>
            }
            title="Deleted with stage"
            listItems={[
              'Stage settings and actions',
              'Any assessment-based interview guide questions if the role has an interview stage',
            ]}
          />
          <ListWithIcon
            icon={
              <div className={clsx(styles.iconContainer, styles.checkIcon)}>
                <Icon icon="check" />
              </div>
            }
            title="Preserved on role"
            listItems={['Candidates']}
          />
        </div>
      </>
    ),
  },
  [ProjectStatus.Draft]: {
    content: (
      <p>
        This action will permanently remove the stage and its associated
        settings from the role and cannot be undone.
      </p>
    ),
  },
  [ProjectStatus.Closed]: {
    content: null,
  },
  [ProjectStatus.Review]: {
    content: null,
  },
};

const DeleteStage: FC<IDeleteStage> = (props) => {
  const { handleMsgType } = useNotification();
  const [modalOpen, setModalOpen] = useState(false);

  const [deleteStageMutation] = useDeleteAssessmentStageMutation({
    refetchQueries: [
      {
        query: ProjectPageGetProjectByIdDocument,
        variables: { id: props.projectId },
      },
    ],
    onCompleted: () => {
      setModalOpen(false);
      handleMsgType({
        type: TNotification.success,
        title: 'Successfully removed assessment stage',
        dismissable: true,
      });
    },
    onError: (error) => {
      handleMsgType({ type: TNotification.error, message: error?.message });
    },
  });

  const handleDelete = () => {
    deleteStageMutation({
      variables: {
        id: props.id,
      },
    });
  };

  return (
    <>
      <Button
        onClick={() => setModalOpen(true)}
        size="medium"
        variant="secondary"
        data-testid="stage-header__delete-stage-button"
        disabled={props.disabled}
      >
        Remove
      </Button>
      <Modal
        header="Remove assessment stage?"
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        headerIconType={IconType.WARNING}
        buttonsProps={{
          confirmButtonChildren: 'Remove stage',
          onConfirmButtonClick: handleDelete,
        }}
      >
        <div className={styles.modalContent}>
          {MODAL_CONTENT[props.projectStatus].content}
        </div>
      </Modal>
    </>
  );
};

export default DeleteStage;
